import type { SupabaseClient } from '@supabase/supabase-js';
import { create } from 'zustand';

interface NotificationStoreState {
  // State
  unreadCount: number;
  isLoading: boolean;

  // Actions
  setUnreadCount: (count: number) => void;
  incrementUnreadCount: () => void;
  decrementUnreadCount: () => void;
  reset: () => void;
  initialize: (supabase: SupabaseClient, userId: string | null) => () => void;
}

const initialState = {
  unreadCount: 0,
  isLoading: false,
};

export const useNotificationStore = create<NotificationStoreState>((set, get) => ({
  ...initialState,

  setUnreadCount: (count: number) => {
    set({ unreadCount: count });
  },

  incrementUnreadCount: () => {
    set(state => ({ unreadCount: state.unreadCount + 1 }));
  },

  decrementUnreadCount: () => {
    set(state => ({ unreadCount: Math.max(0, state.unreadCount - 1) }));
  },

  reset: () => {
    set(initialState);
  },

  initialize: (supabase: SupabaseClient, userId: string | null) => {
    // If no userId, reset and return no-op cleanup
    if (!userId) {
      set({ unreadCount: 0 });
      return () => {};
    }

    // Fetch initial unread count
    supabase
      .from('user_notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .is('read_at', null)
      .then(({ count, error }) => {
        if (error) {
          console.error('Failed to get unread count:', error);
          return;
        }
        set({ unreadCount: count || 0 });
      });

    // Subscribe to realtime notifications
    const channel = supabase
      .channel(`notifications:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_notifications',
          filter: `user_id=eq.${userId}`,
        },
        payload => {
          if (!payload.new) {
            return;
          }
          // Increment unread count if notification is unread
          if (!payload.new.read_at) {
            get().incrementUnreadCount();
          }
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_notifications',
          filter: `user_id=eq.${userId}`,
        },
        payload => {
          if (!payload.new) {
            return;
          }
          // Decrement unread count if notification was marked as read
          if (payload.new.read_at) {
            get().decrementUnreadCount();
          }
        },
      )
      .subscribe();

    // Return cleanup function
    return () => {
      channel.unsubscribe();
    };
  },
}));
